/**
 * Shared syndication formatters (GH #2146 D4 consolidation).
 *
 * The nine syndication pages each carried a private formatCurrencyFull, and
 * they had DRIFTED into two genuinely different behaviors -- consolidating
 * onto a single function would have silently regressed the QA'd surfaces:
 *
 *   formatCurrencyFull  -- exact: always two decimals. Detail / settlement /
 *                          fee surfaces where real cents are the point
 *                          (accrued interest, arranged amounts, fee lines).
 *                          The behavior 7 pages + FeeScheduleDisclosure had.
 *   formatCurrencyGrid  -- the 2026-07-03 QA rule for dense card grids: drop
 *                          cents under five figures, and drop a zero-cents
 *                          (.00) tail at any magnitude -- real cents on
 *                          larger figures survive. The behavior Marketplace
 *                          and CompareDeals had.
 *
 * Both keep the '--' null/undefined sentinel the pages already render.
 * formatCurrencyCompact + formatPercent are the other two duplicated
 * helpers, hoisted here in the same pass.
 *
 * The 2026-09-27 admin.aaim.com sweep (fi-g9-4, -8, -11) hoisted three more
 * families that had forked per page: the date formatters (Marketplace alone
 * printed a two-digit year), the lead-arranger display (three pages, three
 * fallback strings), and the enum label maps (raw SCREAMING_CASE on four
 * pages while Marketplace already carried the loan-type labels).
 *
 * Round 3 (r3 fi-cons-1, fi-cons-2): the exact and compact currency forms are
 * the kit's formatUsd and formatUsdCompact, so one dollar figure prints the
 * same way here as on every other admin page. A ratio (LTV, a share, a
 * haircut) prints at one decimal through formatRatioPercent; an interest rate
 * or yield keeps formatPercent's two decimals, since 6.25% is a real rate.
 */

import { formatUsd, formatUsdCompact } from '@aaimplatform/ui';
import type { components as LspComponents } from '@aaimplatform/institutional-types/loan-syndication-protocol';

type ParticipationOffering = LspComponents['schemas']['ParticipationOffering'];
type ParticipationPosition = LspComponents['schemas']['ParticipationPosition'];
type ParticipationCommitment = LspComponents['schemas']['ParticipationCommitment'];
type ParticipationIoi = LspComponents['schemas']['ParticipationIoi'];
type LoanType = LspComponents['schemas']['LoanDetails']['loanType'];
type OfferingStatus = LspComponents['schemas']['OfferingStatus'];

const CURRENCY_WHOLE = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const PERCENT = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
});

const RATIO_PERCENT = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/** Exact currency, always two decimals (the kit's formatUsd). Detail / settlement / fee surfaces. */
export function formatCurrencyFull(value: number | null | undefined): string {
  if (value === null || value === undefined) return '--';
  return formatUsd(value);
}

/**
 * Dense-grid currency (2026-07-03 QA): amounts under five figures drop cents,
 * and a zero-cents tail adds no information at any magnitude; real cents on
 * larger figures (accrued interest, settlement) stay.
 */
export function formatCurrencyGrid(value: number | null | undefined): string {
  if (value === null || value === undefined) return '--';
  if (Math.abs(value) < 10_000 || Number.isInteger(value)) {
    return CURRENCY_WHOLE.format(value);
  }
  return formatUsd(value);
}

/** Compact currency ($1.2M, $8M) for headline metrics (the kit's formatUsdCompact). */
export function formatCurrencyCompact(value: number | null | undefined): string {
  if (value === null || value === undefined) return '--';
  return formatUsdCompact(value);
}

/**
 * A rate with two decimals; the caller passes a fraction (0.0625 -> 6.25%).
 * Interest rates, coupons and yields only: a ratio uses formatRatioPercent.
 */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '--';
  return PERCENT.format(value);
}

/**
 * A ratio at one decimal, the admin-portal rule for LTV, share and haircut
 * figures (pages/monitoring/display-format.ts): 0.6 -> 60.0%. The marketplace
 * printed LTV at two decimals ('60.00%') beside '65%' on pledge detail and
 * '80.0%' on the monitoring pages (r3 fi-cons-2).
 */
export function formatRatioPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '--';
  return RATIO_PERCENT.format(value);
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

const DATE_FULL: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

/** Full date, 'Sep 27, 2026'. The one date form every syndication page prints. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '--';
  return new Date(value).toLocaleDateString('en-US', DATE_FULL);
}

/**
 * A calendar date the API sends as YYYY-MM-DD (a payment period bound),
 * 'Jun 1, 2026'. Date reads that form as UTC midnight, so it prints in UTC;
 * formatDate would print the day before west of Greenwich.
 */
export function formatCalendarDate(value: string | null | undefined): string {
  if (!value) return '--';
  return new Date(value).toLocaleDateString('en-US', { ...DATE_FULL, timeZone: 'UTC' });
}

/**
 * Compact grid date: 'Sep 22' in the current year, 'Feb 4, 2027' otherwise.
 * The year prints in full: the two-digit form ('Feb 4, 27') read as a day.
 */
export function formatDateShort(
  value: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!value) return '--';
  const d = new Date(value);
  if (d.getFullYear() !== now.getFullYear()) {
    return d.toLocaleDateString('en-US', DATE_FULL);
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Whole days from `now` until `value`, rounded up; negative once it has passed. */
export function daysUntil(
  value: string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime() - now.getTime();
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

/**
 * True once an offering's commitment deadline is behind `now`. An offering
 * with no deadline is never past it. The server still lists such offerings
 * while their status reads OPEN, so the browse grid labels them itself.
 */
export function isPastCommitmentDeadline(
  offering: Pick<ParticipationOffering, 'commitmentDeadline'>,
  now: Date = new Date(),
): boolean {
  if (!offering.commitmentDeadline) return false;
  return new Date(offering.commitmentDeadline).getTime() < now.getTime();
}

// ---------------------------------------------------------------------------
// Lead-arranger display (marketplace masking)
// ---------------------------------------------------------------------------

/**
 * The generic leadInstitutionName the server substitutes on a masked listing.
 * The literal is the contract: schema-registry/loan-syndication-protocol
 * .openapi.yaml ParticipationOffering.leadInstitutionName reads 'Masked to
 * "Disclosed lead arranger" for FI-tenant operator views'. It is compared
 * here, never displayed: it names the opposite of what it means.
 */
export const SPEC_MASKED_LEAD_INSTITUTION_NAME = 'Disclosed lead arranger';

/** What a masked lead reads as on screen. */
export const UNDISCLOSED_LEAD_LABEL = 'Undisclosed lead arranger';

type LeadIdentity = Pick<
  LspComponents['schemas']['ParticipationOffering'],
  'disclosedLeadArranger' | 'leadInstitutionName'
>;

/**
 * True when the server withheld the lead's identity. A listing the caller's
 * own institution leads is not masked (the server masks counterparties only),
 * and neither is a platform-operator view, so an undisclosed flag alone does
 * not mean masked: the name has to be absent or the masked placeholder.
 */
export function isLeadMasked(offering: LeadIdentity): boolean {
  if (offering.disclosedLeadArranger) return false;
  return (
    !offering.leadInstitutionName
    || offering.leadInstitutionName === SPEC_MASKED_LEAD_INSTITUTION_NAME
  );
}

/** The lead name, or the undisclosed label when the server masked it. */
export function formatLeadDisplay(offering: LeadIdentity): string {
  if (isLeadMasked(offering) || !offering.leadInstitutionName) return UNDISCLOSED_LEAD_LABEL;
  return offering.leadInstitutionName;
}

// ---------------------------------------------------------------------------
// Enum labels -- raw codes never render on screen
// ---------------------------------------------------------------------------

export const LOAN_TYPE_LABELS: Record<LoanType, string> = {
  PLEDGED_ASSET: 'Pledged asset',
  SECURITIES_BACKED: 'Securities-backed',
  DIGITAL_ASSET_BACKED: 'Digital-asset-backed',
  OVERDRAFT_RECEIVABLES_POOL: 'Overdraft receivables pool',
};

export const OFFERING_STATUS_LABELS: Record<OfferingStatus, string> = {
  OPEN: 'Open',
  FULLY_COMMITTED: 'Fully committed',
  ACTIVE: 'Active',
  MATURED: 'Matured',
  DEFAULTED: 'Defaulted',
  // term-register-skip: CANCELLED is the spec enum key; the label is US spelling
  CANCELLED: 'Canceled',
};

export const POSITION_STATUS_LABELS: Record<ParticipationPosition['status'], string> = {
  ACTIVE: 'Active',
  MATURED: 'Matured',
  DEFAULTED: 'Defaulted',
  EXITED: 'Exited',
};

export const PARTICIPATION_ROLE_LABELS: Record<ParticipationPosition['role'], string> = {
  LEAD: 'Lead',
  PARTICIPANT: 'Participant',
};

export const COMMITMENT_STATUS_LABELS: Record<ParticipationCommitment['status'], string> = {
  PENDING_COAGENT_APPROVAL: 'Pending co-agent approval',
  COMMITTED: 'Committed',
  ACTIVATED: 'Activated',
  WITHDRAWN: 'Withdrawn',
  RELEASED: 'Released',
};

export const IOI_STATUS_LABELS: Record<ParticipationIoi['status'], string> = {
  SUBMITTED: 'Submitted',
  ALLOCATION_OFFERED: 'Allocation offered',
  ACCEPTED: 'Accepted',
  DECLINED: 'Declined',
  WITHDRAWN: 'Withdrawn',
  EXPIRED: 'Expired',
};

/** 'PRIVATE_CREDIT' -> 'Private credit'. The fallback for a code no map carries. */
export function humanizeCode(value: string): string {
  const words = value.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Label a value through its map. A value the map does not carry is
 * humanized when it is a SCREAMING_CASE code, and returned as written
 * otherwise (free text such as a loan record's own collateral description).
 */
export function labelFor(labels: Readonly<Record<string, string>>, value: string): string {
  const mapped = labels[value];
  if (mapped) return mapped;
  return /^[A-Z][A-Z0-9_]*$/.test(value) ? humanizeCode(value) : value;
}

/**
 * A listing's asset type in words: the collateral's taxonomy class when the
 * listing carries one ('REAL_ESTATE' -> 'Real estate'), otherwise its loan
 * type through LOAN_TYPE_LABELS. The Brief drawer printed both codes raw
 * (r3 fi-g9-7).
 */
export function formatAssetType(
  offering: Pick<ParticipationOffering, 'collateralSummary' | 'loanDetails'>,
): string {
  const assetClass = offering.collateralSummary?.primaryAssetClass;
  if (assetClass) return labelFor({}, assetClass);
  return LOAN_TYPE_LABELS[offering.loanDetails.loanType];
}

/**
 * Who has committed to a listing, in the unit the response states.
 * counterpartyCount is distinct participants
 * (ParticipationOffering.counterpartyCount); commitmentCount counts
 * commitment rows, and one participant may hold several. The grid labeled
 * either as '{n} committed' and the drawer read it as 'Participants'
 * (r3 fi-g9-6), so each count now names its own unit. Null when the response
 * carries neither.
 */
export function formatCommitted(
  offering: Pick<ParticipationOffering, 'counterpartyCount' | 'commitmentCount'>,
): string | null {
  if (typeof offering.counterpartyCount === 'number') {
    const n = offering.counterpartyCount;
    return `${n} participant${n === 1 ? '' : 's'}`;
  }
  if (typeof offering.commitmentCount === 'number') {
    const n = offering.commitmentCount;
    return `${n} commitment${n === 1 ? '' : 's'}`;
  }
  return null;
}
